'use client'

/**
 * The SchoolAI side navigation (#15618) — every signed-in viewer's only
 * navigation since #16727 retired its flag and the top-nav path.
 *
 * Follows the classroom product's shell (SchoolAI/web-app,
 * `_features/navigation/app-navigation.tsx` and `primary-navigation.tsx`) and
 * the admin-tool design pass prototype (`schoolai-design-sandbox`,
 * `src/chrome/AppFrame.tsx`):
 *
 *   - HEADER: sai-ui's SchoolAI `Logo` (to the org's landing page; hidden on
 *     the collapsed rail, as live does) and the desktop collapse trigger.
 *   - MAIN, centred: the org's saved `left` list, in its order.
 *   - BOTTOM, above the account row and its menu (`account-menu.tsx`): the
 *     saved `right` list, in its order.
 *   The org's nav configuration is followed exactly (#15668): Search,
 *   Notifications and Feedback sit wherever it places them, and an org with
 *   nothing saved gets `defaultSidebarNavLayout`. Any entry of the one
 *   vocabulary may be placed (#15689): a page draws as a link, an action
 *   (Copy Page Link, AI Chat, …) as a button running `entryAction`. No new
 *   gate: availability is `buildEntryAvailability`, the header's own map.
 *   Each row carries its `nav-<key>` testid.
 *
 * sai-ui's own layout is kept, as the classroom product keeps it (#15668):
 * the main list centres vertically in `SidebarContent`, no separator, no
 * border, and the sidebar is the page's grey, not a white panel: sai-ui's
 * `surface-default`, which Cadence uses as shipped since #16837
 * (`SIDEBAR_STICKY_PANEL_SURFACE` restates it on the inner element).
 *
 * Every action opens the SAME instance the header owns — the command bar, the
 * shortcuts drawer and the feedback modal are read through
 * `app-chrome-context`, never mounted twice.
 */

import {
  Logo,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from '@sai/ui/components'
import { useSidebar } from '@sai/ui/hooks'
import type { Route } from 'next'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useContext } from 'react'

import { entryAction } from '@/components/entry-actions'
import { navEntryFace } from '@/components/header-nav-entries'
import { TerminologyContext } from '@/contexts/terminology-context'
import { buildEntryAvailability } from '@/lib/app-chrome/nav-availability'
import {
  defaultSidebarNavLayout,
  resolveNavLayout,
} from '@/lib/org-nav/resolve'
import { ENTRIES, type EntryKey } from '@/lib/org-nav/vocabulary'
import { cn } from '@/lib/utils'
import { SIDEBAR_STICKY_PANEL_SURFACE } from '@/lib/sticky-surface-classes'

import type { AccountMenuUser } from './account-menu'
import { activeNavKey } from './active-nav'
import { trackSideNavEvent } from './side-nav-tracking'
import {
  SIDEBAR_CONTROL_FOCUS_RING,
  SIDEBAR_ROW_FOCUS_RING,
} from './sidebar-focus-ring'
import {
  type AppSidebarChrome,
  chromeGateInputs,
  SidebarAccountMenu,
  useEntryActionContext,
} from './sidebar-account-menu'
import { SidebarNotificationsItem } from './sidebar-notifications-item'
import { SIDEBAR_FIXED_PLACEMENT_CLASS } from './sidebar-placement'
import { SidebarFeedbackItem, SidebarSearchItem } from './sidebar-utility-items'

export type { AppSidebarChrome }

export interface AppSidebarProps {
  user: AccountMenuUser
  /** The brand link's target: the org's landing page, or `/today`. */
  landingHref: string
  chrome: AppSidebarChrome
}

/** Hidden on the collapsed icon rail. */
const RAIL_HIDDEN = 'group-data-[collapsible=icon]:hidden'

export function AppSidebar({ user, landingHref, chrome }: AppSidebarProps) {
  const pathname = usePathname() ?? ''
  const { open, isMobile, setOpenMobile } = useSidebar()
  const terminology = useContext(TerminologyContext)

  const actionContext = useEntryActionContext(chrome)

  const resolved = resolveNavLayout(
    chrome.navLayout,
    buildEntryAvailability(chromeGateInputs(chrome, user.role)),
    defaultSidebarNavLayout,
  )
  const active = activeNavKey(pathname, [...resolved.left, ...resolved.right])

  /** A followed link closes the mobile sheet. */
  const closeSheet = () => {
    if (isMobile) setOpenMobile(false)
  }

  /** #15689 — an action placed in either zone: a button that runs it. */
  const renderAction = (key: EntryKey) => {
    const action = entryAction(key, actionContext)
    if (!action) return null
    return (
      <SidebarMenuItem key={key}>
        <SidebarMenuButton
          tooltip={action.label}
          className={SIDEBAR_ROW_FOCUS_RING}
          data-testid={`nav-${key}`}
          disabled={action.disabled}
          aria-keyshortcuts={
            action.shortcut ? `Shift+${action.shortcut}` : undefined
          }
          onClick={() => {
            trackSideNavEvent({ event: 'item_clicked', key })
            closeSheet()
            action.run()
          }}
        >
          <action.Icon />
          <span>{action.label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  const renderDestination = (key: EntryKey) => {
    const face = navEntryFace(key, terminology)
    const isActive = active === key
    return (
      <SidebarMenuItem key={key}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          tooltip={face.label}
          className={SIDEBAR_ROW_FOCUS_RING}
        >
          <Link
            href={face.href as Route}
            data-testid={`nav-${key}`}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => {
              trackSideNavEvent({ event: 'item_clicked', key })
              closeSheet()
            }}
          >
            <face.Icon />
            <span>{face.label}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  /** One row of either zone: a control, an action, or a destination. */
  const renderEntry = (key: EntryKey) => {
    if (key === 'search') {
      return <SidebarSearchItem key={key} onNavigate={closeSheet} />
    }
    if (key === 'notifications') {
      return (
        <SidebarNotificationsItem
          key={key}
          userId={user.id}
          isActive={pathname === '/notifications'}
          onNavigate={closeSheet}
        />
      )
    }
    if (key === 'feedback') {
      return <SidebarFeedbackItem key={key} onNavigate={closeSheet} />
    }
    return ENTRIES[key].href === null
      ? renderAction(key)
      : renderDestination(key)
  }

  return (
    <Sidebar
      ariaLabel="App Primary Nav"
      ariaDescription="Navigate between the areas of SchoolAI Admin"
      collapsible="icon"
      data-testid="app-sidebar"
      // Placement (z-index, below the banners, anchored to the viewport's
      // bottom — never `svh`, #16342) lives in `sidebar-placement.ts`. The
      // fill is the page's grey, opaque under sai (see the header comment).
      className={cn(
        SIDEBAR_FIXED_PLACEMENT_CLASS,
        SIDEBAR_STICKY_PANEL_SURFACE,
      )}
    >
      <SidebarHeader className="flex-col items-stretch">
        <div className="flex w-full items-center justify-between">
          {/* The classroom product's `SidebarLogo`, sizes and all. */}
          <Link
            href={landingHref as Route}
            className={`flex h-10 items-center rounded-md p-2 outline-hidden ${SIDEBAR_CONTROL_FOCUS_RING} [&>svg]:-ms-[5.5px] [&>svg]:h-4.5 [&>svg]:w-[94px] ${RAIL_HIDDEN}`}
            aria-label="SchoolAI Admin home"
            onClick={closeSheet}
          >
            <Logo decorative logo="schoolai" />
          </Link>
          <SidebarTrigger
            ariaLabel={open ? 'Collapse sidebar' : 'Expand sidebar'}
            trackingId="app-sidebar-collapse"
            iconName="collapse-left"
            className={cn('hidden md:inline-flex', SIDEBAR_CONTROL_FOCUS_RING)}
            onClick={() =>
              trackSideNavEvent({ event: 'collapsed', collapsed: open })
            }
          />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>{resolved.left.map(renderEntry)}</SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {resolved.right.map(renderEntry)}
          <SidebarMenuItem>
            <SidebarAccountMenu user={user} chrome={chrome} />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
